import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
} from "react";
import {
  Download,
  Edit2,
  Plus,
  Printer,
  Save,
  Settings2,
  Trash2,
  FileText,
  List,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Select as AntSelect } from "antd";
import { notify } from "@/components/common/Notification";
import {
  extractApiSuccessMessage,
  getApiErrorMessage,
} from "@/utils/extractApiErrorMessage";
import {
  DropdownService,
  DynamicModuleService,
  PtsBillingService,
  PtsProjectService,
} from "@/services/APIs";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { Textarea } from "../../../components/ui/textarea";
import { Tabs, TabsContent } from "../../../components/ui/tabs";
import {
  PtsPillTabsList,
  PtsPillTabsTrigger,
} from "../../../components/PtsPillTabs";
import {
  BillingConfigListingSection,
  InvoicePreviewListingSection,
} from "./ListingSections";
import InvoicePreviewPaymentSection from "./InvoicePreviewPaymentSection";
import { DEFAULT_PAYMENT_TERMS, INVOICE_FROM } from "./data";
import {
  BILLING_FREQUENCY_OPTIONS,
  buildPreviewSubtitle,
  calculateInvoiceTotals,
  calculateLineItemTax,
  getFrequencyMonths,
  getGstTaxRowCount,
  isInstateGstType,
  extractBillingTypeSubtitle,
  formatBillingPeriod,
  addDaysToDateInput,
  getBillingTypeRecordId,
  type BillingFrequency,
} from "./billingPeriodUtils";
import { buildSaveBillingPayload } from "./billingSaveUtils";
import {
  notifyBillingApiErrors,
  extractBillingConfigurationId,
  extractApiValidationMessages,
} from "./billingValidationUtils";
import { buildSaveInvoicePayload } from "./invoiceSaveUtils";
import {
  BILLING_CONFIGURATIONS_QUERY_KEY,
  BillingConfigurationApiRecord,
} from "./billingListUtils";
import { INVOICE_PREVIEWS_QUERY_KEY } from "./invoiceListUtils";
import {
  mapBillingRecordToForm,
  enrichLineItemPreviewDisplay,
} from "./billingEditUtils";
import {
  INVOICE_REMINDER_QUERY_KEY,
  mapInvoiceReminderToBillingConfiguration,
} from "./invoiceReminderUtils";
import {
  inr,
  buildBillToFromProjectClient,
  EMPTY_BILL_TO,
  getProjectGstDefaults,
  formatInvoiceAmount,
} from "./utils";
import type { InvoicePreviewLineItem, ProjectClientData } from "./types";
import {
  downloadInvoicePdf,
  formatInvoiceDateForPdf,
  printInvoicePdf,
  type InvoicePdfData,
} from "./generateInvoicePdf";
import UsLogo from "../../../assets/us-logo.png";

const PTS_BILLING_TYPE_SCHEMA_NAME = "PTS Billing Type";
const PTS_CURRENCY_SCHEMA_NAME = "PTS Currency";

type DropdownOption = {
  value: string;
  label: string;
  subtitle?: string;
};

function normalizeDropdownOptions(data: unknown): DropdownOption[] {
  if (!data) {
    return [];
  }

  const list = Array.isArray(data)
    ? data
    : ((data as { results?: unknown[]; data?: unknown[] })?.results ??
      (data as { results?: unknown[]; data?: unknown[] })?.data ??
      []);

  return (
    list as Array<{
      value?: string;
      label?: string;
      id?: string;
      name?: string;
      subtitle?: string;
      sub_title?: string;
    }>
  )
    .map(item => {
      const subtitle = String(item?.subtitle ?? item?.sub_title ?? "").trim();
      return {
        value: String(item?.value ?? item?.id ?? ""),
        label: String(
          item?.label ?? item?.name ?? item?.value ?? item?.id ?? ""
        ),
        ...(subtitle ? { subtitle } : {}),
      };
    })
    .filter(item => item.value);
}

function resolveDefaultCurrencyValue(options: DropdownOption[]): string {
  if (options.length === 0) {
    return "";
  }

  const inrOption = options.find(option => {
    const label = option.label.trim().toUpperCase();
    const subtitle = (option.subtitle ?? "").trim().toUpperCase();
    return (
      label === "INR" ||
      subtitle === "INR" ||
      label.includes("INR") ||
      subtitle.includes("INR")
    );
  });

  return inrOption?.value ?? options[0]?.value ?? "";
}

function getTodayDateInputValue(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const DEFAULT_ITEM_TITLE = "Software License Renewal";
const DEFAULT_LINE_ITEM_AMOUNT = "0";
const DEFAULT_LINE_ITEM_HSN_SAC = "123456";
const DEFAULT_LINE_ITEM_QTY = "1";
const DEFAULT_INVOICE_NUMBER = "01/26-27";
const DEFAULT_INVOICE_TITLE = "Performa Invoice";
const DEFAULT_CC_EMAILS =
  "darshils@universal-software.com, kushagra@universal-software.com, sandeep@universal-software.com";
// "darshils@universal-software.com, sandeep@universal-software.com, kushagra@universal-software.com";
const DEFAULT_ACCOUNTANT_REVIEW_EMAIL = "darshils@universal-software.com";
const DEFAULT_REMINDER_DAYS = "3";

interface BillingConfigTabProps {
  editBillingId?: string;
  editNonce?: string;
}

const BillingConfigTab = ({
  editBillingId,
  editNonce,
}: BillingConfigTabProps) => {
  const queryClient = useQueryClient();
  const lastLoadedEditKeyRef = useRef<string | null>(null);
  // const [enabled, setEnabled] = useState(true);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(
    undefined
  );
  const [gstApplicable, setGstApplicable] = useState(false);
  const [billingType, setBillingType] = useState("");
  const [frequency, setFrequency] = useState<BillingFrequency | "">("");
  const [billingStartDate, setBillingStartDate] = useState("");
  const [currency, setCurrency] = useState("");
  const [gstRate, setGstRate] = useState("");
  const [amount, setAmount] = useState(DEFAULT_LINE_ITEM_AMOUNT);
  const [invoiceNumber, setInvoiceNumber] = useState(DEFAULT_INVOICE_NUMBER);
  const [invoiceDate, setInvoiceDate] = useState(() =>
    getTodayDateInputValue()
  );
  const [invoiceTitle, setInvoiceTitle] = useState(DEFAULT_INVOICE_TITLE);
  const [hsnSac, setHsnSac] = useState(DEFAULT_LINE_ITEM_HSN_SAC);
  const [qty, setQty] = useState(DEFAULT_LINE_ITEM_QTY);
  const [lineItemFormResetKey, setLineItemFormResetKey] = useState(0);
  const [notes, setNotes] = useState("");
  const [gracePeriodDays, setGracePeriodDays] = useState(
    String(DEFAULT_PAYMENT_TERMS.dueDays)
  );
  const [clientBillingEmail, setClientBillingEmail] = useState("");
  const [ccEmails, setCcEmails] = useState(DEFAULT_CC_EMAILS);
  const [accountantReviewEmail, setAccountantReviewEmail] = useState(
    DEFAULT_ACCOUNTANT_REVIEW_EMAIL
  );
  const [reminderDays, setReminderDays] = useState(DEFAULT_REMINDER_DAYS);
  const [nextReminderDate, setNextReminderDate] = useState("");
  const [invoiceLineItems, setInvoiceLineItems] = useState<
    InvoicePreviewLineItem[]
  >([]);
  const [editingLineItemId, setEditingLineItemId] = useState<string | null>(
    null
  );
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);
  const [isSavingConfiguration, setIsSavingConfiguration] = useState(false);
  const [editingConfigurationId, setEditingConfigurationId] = useState<
    string | null
  >(null);
  const [configurationGstType, setConfigurationGstType] = useState<
    string | undefined
  >(undefined);

  const { data: billingTypeOptionsRaw, isLoading: billingTypeLoading } =
    useQuery({
      queryKey: ["dropdown", PTS_BILLING_TYPE_SCHEMA_NAME],
      queryFn: () =>
        DropdownService.getDropdownData(PTS_BILLING_TYPE_SCHEMA_NAME),
    });

  const { data: billingTypeRecordsResponse } = useQuery({
    queryKey: ["moduleData", PTS_BILLING_TYPE_SCHEMA_NAME, "billing-config"],
    queryFn: () =>
      DynamicModuleService.getGridColItems(
        PTS_BILLING_TYPE_SCHEMA_NAME,
        1,
        1000
      ),
  });

  const { data: currencyOptionsRaw, isLoading: currencyLoading } = useQuery({
    queryKey: ["dropdown", PTS_CURRENCY_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(PTS_CURRENCY_SCHEMA_NAME),
  });

  const { data: projectOptions, isLoading: projectLoading } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const { data: invoiceReminders = [] } = useQuery({
    queryKey: INVOICE_REMINDER_QUERY_KEY,
    queryFn: () => PtsBillingService.getInvoiceReminders(),
  });

  const { data: projectClientResponse, isLoading: projectClientLoading } =
    useQuery({
      queryKey: ["project-client-data", projectId],
      queryFn: () => PtsProjectService.getProjectClientData(projectId!),
      enabled: Boolean(projectId),
    });

  const billingTypeOptions = useMemo(
    () => normalizeDropdownOptions(billingTypeOptionsRaw),
    [billingTypeOptionsRaw]
  );

  const currencyOptions = useMemo(
    () => normalizeDropdownOptions(currencyOptionsRaw),
    [currencyOptionsRaw]
  );

  const defaultCurrencyValue = useMemo(
    () => resolveDefaultCurrencyValue(currencyOptions),
    [currencyOptions]
  );

  useEffect(() => {
    if (!defaultCurrencyValue) {
      return;
    }

    setCurrency(prev => (prev.trim() ? prev : defaultCurrencyValue));
  }, [defaultCurrencyValue]);

  const projectSelectOptions = useMemo(
    () =>
      (projectOptions ?? []).map(option => ({
        value: option.value,
        label: option.label,
      })),
    [projectOptions]
  );

  const projectClients = useMemo(() => {
    const clients = projectClientResponse?.data;
    if (!Array.isArray(clients)) {
      return [] as ProjectClientData[];
    }

    return clients.filter(client => client.is_active !== false);
  }, [projectClientResponse]);

  const clientSelectOptions = useMemo(
    () =>
      projectClients.map(client => ({
        value: client.id,
        label: client.client_name?.trim() || "Unnamed client",
      })),
    [projectClients]
  );

  useEffect(() => {
    setSelectedClientId(undefined);
  }, [projectId]);

  useEffect(() => {
    if (projectClients.length === 0) {
      return;
    }

    const selectedStillExists = projectClients.some(
      client => client.id === selectedClientId
    );
    if (!selectedClientId || !selectedStillExists) {
      setSelectedClientId(projectClients[0].id);
    }
  }, [projectClients, selectedClientId]);

  const selectedProjectClient = useMemo(
    () =>
      projectClients.find(client => client.id === selectedClientId) ??
      projectClients[0],
    [projectClients, selectedClientId]
  );

  useEffect(() => {
    if (!selectedProjectClient?.email?.trim()) {
      return;
    }

    setClientBillingEmail(selectedProjectClient.email.trim());
  }, [selectedProjectClient?.id, selectedProjectClient?.email]);

  useEffect(() => {
    if (
      !projectId ||
      editingLineItemId ||
      editingConfigurationId ||
      projectClientLoading
    ) {
      return;
    }

    const gstDefaults = getProjectGstDefaults(selectedProjectClient);
    setGstApplicable(gstDefaults.applicable);
    setGstRate(gstDefaults.rate);
  }, [
    projectId,
    selectedProjectClient,
    projectClientLoading,
    editingLineItemId,
    editingConfigurationId,
  ]);

  const billTo = useMemo(() => {
    if (!projectId) {
      return EMPTY_BILL_TO;
    }

    if (projectClientLoading) {
      return {
        name: "",
        lines: ["Loading client details..."],
        gstin: "",
      };
    }

    if (!selectedProjectClient) {
      return {
        name: "",
        lines: ["No client found for this project"],
        gstin: "",
      };
    }

    return buildBillToFromProjectClient(selectedProjectClient);
  }, [projectId, projectClientLoading, selectedProjectClient]);

  const billingTypeRecords = useMemo(() => {
    const raw =
      billingTypeRecordsResponse?.results ?? billingTypeRecordsResponse ?? [];
    return Array.isArray(raw) ? (raw as Record<string, unknown>[]) : [];
  }, [billingTypeRecordsResponse]);

  const selectedBillingTypeRecord = useMemo(
    () =>
      billingTypeRecords.find(
        record => getBillingTypeRecordId(record) === billingType
      ),
    [billingType, billingTypeRecords]
  );

  const selectedBillingTypeLabel = useMemo(
    () =>
      billingTypeOptions.find(option => option.value === billingType)?.label ??
      "",
    [billingType, billingTypeOptions]
  );

  const billingTypeSchemaSubtitle = useMemo(() => {
    const fromDropdown = billingTypeOptions.find(
      option => option.value === billingType
    )?.subtitle;
    if (fromDropdown) {
      return fromDropdown;
    }

    const fromRecord = extractBillingTypeSubtitle(selectedBillingTypeRecord);
    if (fromRecord) {
      return fromRecord;
    }

    return selectedBillingTypeLabel;
  }, [
    billingType,
    billingTypeOptions,
    selectedBillingTypeRecord,
    selectedBillingTypeLabel,
  ]);

  const selectedFrequencyLabel = useMemo(
    () =>
      BILLING_FREQUENCY_OPTIONS.find(option => option.value === frequency)
        ?.label ?? "",
    [frequency]
  );

  const previewItemTitle = useMemo(
    () =>
      billingTypeSchemaSubtitle ||
      selectedBillingTypeLabel ||
      DEFAULT_ITEM_TITLE,
    [billingTypeSchemaSubtitle, selectedBillingTypeLabel]
  );

  const previewSubtitle = useMemo(
    () =>
      buildPreviewSubtitle(selectedBillingTypeLabel, selectedFrequencyLabel),
    [selectedBillingTypeLabel, selectedFrequencyLabel]
  );

  useEffect(() => {
    if (!editingConfigurationId || invoiceLineItems.length === 0) {
      return;
    }

    if (billingTypeOptions.length === 0) {
      return;
    }

    setInvoiceLineItems(prev => {
      const next = prev.map(item =>
        enrichLineItemPreviewDisplay(
          item,
          billingTypeOptions,
          billingTypeRecords
        )
      );

      const hasChanges = next.some(
        (item, index) =>
          item.title !== prev[index]?.title ||
          item.subtitle !== prev[index]?.subtitle ||
          item.billingPeriod !== prev[index]?.billingPeriod
      );

      return hasChanges ? next : prev;
    });
  }, [
    editingConfigurationId,
    billingTypeOptions,
    billingTypeRecords,
    invoiceLineItems.length,
  ]);

  const billingPeriodLabel = useMemo(
    () =>
      billingStartDate && frequency
        ? formatBillingPeriod(billingStartDate, frequency as BillingFrequency)
        : "—",
    [billingStartDate, frequency]
  );

  const clearLineItemFormFields = useCallback(() => {
    setBillingType("");
    setFrequency("");
    setBillingStartDate("");
    setAmount(DEFAULT_LINE_ITEM_AMOUNT);
    setCurrency(defaultCurrencyValue);
    setHsnSac(DEFAULT_LINE_ITEM_HSN_SAC);
    setQty(DEFAULT_LINE_ITEM_QTY);
    setEditingLineItemId(null);
    setLineItemFormResetKey(prev => prev + 1);
  }, [defaultCurrencyValue]);

  const handleFrequencyChange = (value: BillingFrequency) => {
    setFrequency(value);
    // One Time always uses qty 1; other frequencies use month count
    setQty(value === "onetime" ? "1" : String(getFrequencyMonths(value) || 1));
  };

  const resetBillingFormFields = () => {
    clearLineItemFormFields();
    const gstDefaults = getProjectGstDefaults(selectedProjectClient);
    setGstApplicable(gstDefaults.applicable);
    setGstRate(gstDefaults.rate);
  };

  const handleProjectChange = (value: string | undefined) => {
    const nextProjectId = value ?? undefined;
    if (nextProjectId === projectId) {
      return;
    }

    const itemCount = invoiceLineItems.length;
    if (itemCount > 0) {
      setInvoiceLineItems([]);
      notify.warning(
        "Invoice items removed",
        `Changing the project cleared ${itemCount} item${itemCount > 1 ? "s" : ""} from the preview.`
      );
    }

    clearLineItemFormFields();
    setGstApplicable(false);
    setGstRate("");
    setProjectId(nextProjectId);
    setSelectedClientId(undefined);
  };

  const clientGstType =
    configurationGstType ?? selectedProjectClient?.gst_types;
  const isInstateGst = isInstateGstType(clientGstType);

  const invoiceTotals = useMemo(
    () => calculateInvoiceTotals(invoiceLineItems, clientGstType),
    [invoiceLineItems, clientGstType]
  );

  const buildLineItemFromForm = (id: string): InvoicePreviewLineItem => {
    const rate = Number(amount) || 0;
    const quantity = Number(qty) || 0;

    return {
      id,
      title: previewItemTitle || DEFAULT_ITEM_TITLE,
      subtitle: previewSubtitle,
      hsnSac,
      qty: quantity,
      rate,
      amount: rate * quantity,
      billingPeriod: billingPeriodLabel,
      gstApplicable,
      gstRate: Number(gstRate) || 0,
      billingType,
      frequency,
      billingStartDate,
      currency,
    };
  };

  const validateLineItemForm = (): boolean => {
    if (!projectId) {
      notify.error("Please select a project");
      return false;
    }

    if (!billingType) {
      notify.error("Please select a billing type");
      return false;
    }

    if (!frequency) {
      notify.error("Please select a frequency");
      return false;
    }

    if (!billingStartDate.trim()) {
      notify.error("Please select a billing start date");
      return false;
    }

    if (!nextReminderDate.trim()) {
      notify.error("Please select a next reminder date");
      return false;
    }

    const rate = Number(amount) || 0;
    if (rate <= 0) {
      notify.error("Please enter a valid amount");
      return false;
    }

    if (!currency.trim()) {
      notify.error("Please select a currency");
      return false;
    }

    if (!hsnSac.trim()) {
      notify.error("Please enter HSN/SAC");
      return false;
    }

    const quantity = Number(qty) || 0;
    if (quantity <= 0) {
      notify.error("Please enter a valid quantity");
      return false;
    }

    if (gstApplicable && !(Number(gstRate) > 0)) {
      notify.error("Please enter a valid GST rate");
      return false;
    }

    return true;
  };

  const handleAddToPreview = () => {
    if (!validateLineItemForm()) {
      return;
    }

    if (editingLineItemId) {
      const updatedItem = buildLineItemFromForm(editingLineItemId);
      setInvoiceLineItems(prev =>
        prev.map(item => (item.id === editingLineItemId ? updatedItem : item))
      );
      notify.success("Line item updated");
      resetBillingFormFields();
      return;
    }

    const lineItem = buildLineItemFromForm(
      `${Date.now()}-${invoiceLineItems.length + 1}`
    );

    setInvoiceLineItems(prev => [...prev, lineItem]);
    notify.success("Invoice item added to invoice preview");
    resetBillingFormFields();
  };

  const handleEditLineItem = (item: InvoicePreviewLineItem) => {
    setEditingLineItemId(item.id);
    setBillingType(item.billingType || billingType);
    setFrequency((item.frequency as BillingFrequency) || frequency);
    setBillingStartDate(item.billingStartDate || billingStartDate);
    setAmount(String(item.rate));
    setHsnSac(item.hsnSac);
    setQty(String(item.qty));
    setGstApplicable(item.gstApplicable);
    setGstRate(String(item.gstRate));
    if (item.currency) {
      setCurrency(item.currency);
    }
  };

  const handleDeleteLineItem = (id: string) => {
    setInvoiceLineItems(prev => prev.filter(item => item.id !== id));
    if (editingLineItemId === id) {
      setEditingLineItemId(null);
    }
    notify.success("Invoice item removed");
  };

  const handleCancelLineItemEdit = () => {
    resetBillingFormFields();
  };

  const resetConfigurationAndInvoicePreview = () => {
    setEditingConfigurationId(null);
    setConfigurationGstType(undefined);
    setEditingLineItemId(null);
    setProjectId(undefined);
    setSelectedClientId(undefined);
    setGstApplicable(false);
    setGstRate("");
    setBillingType("");
    setFrequency("");
    setBillingStartDate("");
    setCurrency("");
    setAmount(DEFAULT_LINE_ITEM_AMOUNT);
    setHsnSac(DEFAULT_LINE_ITEM_HSN_SAC);
    setQty(DEFAULT_LINE_ITEM_QTY);
    setClientBillingEmail("");
    setCcEmails(DEFAULT_CC_EMAILS);
    setAccountantReviewEmail(DEFAULT_ACCOUNTANT_REVIEW_EMAIL);
    setReminderDays(DEFAULT_REMINDER_DAYS);
    setNextReminderDate("");
    setGracePeriodDays(String(DEFAULT_PAYMENT_TERMS.dueDays));
    setNotes("");
    setInvoiceLineItems([]);
    setInvoiceNumber(DEFAULT_INVOICE_NUMBER);
    setInvoiceDate(getTodayDateInputValue());
    setInvoiceTitle(DEFAULT_INVOICE_TITLE);
    setLineItemFormResetKey(prev => prev + 1);
  };

  const handleCancelConfigurationEdit = () => {
    if (editBillingId) {
      lastLoadedEditKeyRef.current = `${editBillingId}:${editNonce ?? ""}`;
    } else {
      lastLoadedEditKeyRef.current = null;
    }
    resetConfigurationAndInvoicePreview();
  };

  const handleEditBillingConfiguration = useCallback(
    (record: BillingConfigurationApiRecord, options?: { silent?: boolean }) => {
      if (!record.id) {
        notify.error("Unable to edit this configuration");
        return;
      }

      const form = mapBillingRecordToForm(record, {
        billingTypeOptions,
        currencyOptions,
        projectOptions: projectOptions ?? [],
        billingTypeRecords,
      });

      setEditingConfigurationId(record.id);
      setConfigurationGstType(form.configurationGstType);
      setProjectId(form.projectId);
      setSelectedClientId(undefined);
      setInvoiceNumber(form.invoiceNumber || DEFAULT_INVOICE_NUMBER);
      setClientBillingEmail(form.clientBillingEmail);
      setCcEmails(form.ccEmails);
      setAccountantReviewEmail(form.accountantReviewEmail);
      setReminderDays(form.reminderDays);
      setNextReminderDate(form.nextReminderDate);
      setGracePeriodDays(form.gracePeriodDays);
      setNotes(form.notes);
      setInvoiceLineItems(form.lineItems);
      setEditingLineItemId(null);
      clearLineItemFormFields();

      if (form.billingType) {
        setBillingType(form.billingType);
      }
      if (form.frequency) {
        setFrequency(form.frequency);
      }
      if (form.billingStartDate) {
        setBillingStartDate(form.billingStartDate);
      }

      const firstLineItem = form.lineItems[0];
      if (firstLineItem) {
        setGstApplicable(firstLineItem.gstApplicable);
        setGstRate(String(firstLineItem.gstRate));
      }

      lastLoadedEditKeyRef.current = `${record.id}:${editNonce ?? ""}`;
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (!options?.silent) {
        notify.success("Configuration loaded for editing");
      }
    },
    [
      billingTypeOptions,
      billingTypeRecords,
      clearLineItemFormFields,
      currencyOptions,
      editNonce,
      projectOptions,
    ]
  );

  useEffect(() => {
    if (!editBillingId) {
      lastLoadedEditKeyRef.current = null;
      return;
    }

    const editKey = `${editBillingId}:${editNonce ?? ""}`;
    if (lastLoadedEditKeyRef.current === editKey) {
      return;
    }

    if (
      billingTypeLoading ||
      currencyLoading ||
      projectLoading ||
      billingTypeOptions.length === 0 ||
      currencyOptions.length === 0
    ) {
      return;
    }

    const reminder = invoiceReminders.find(item => item.id === editBillingId);
    if (!reminder?.source) {
      return;
    }

    handleEditBillingConfiguration(
      mapInvoiceReminderToBillingConfiguration(reminder.source),
      { silent: true }
    );
    if (reminder.source.invoice_title?.trim()) {
      setInvoiceTitle(reminder.source.invoice_title.trim());
    }
    notify.success("Reminder loaded into billing configuration");
  }, [
    editBillingId,
    editNonce,
    invoiceReminders,
    billingTypeLoading,
    currencyLoading,
    projectLoading,
    billingTypeOptions,
    currencyOptions,
    projectOptions,
    billingTypeRecords,
    handleEditBillingConfiguration,
  ]);

  const handleDeleteBillingConfiguration = async (
    record: BillingConfigurationApiRecord
  ) => {
    if (!record.id) {
      notify.error("Unable to delete this configuration");
      return;
    }

    try {
      const response = await PtsBillingService.deleteBillingConfiguration(
        record.id
      );

      if (response === undefined) {
        return;
      }

      if (editingConfigurationId === record.id) {
        handleCancelConfigurationEdit();
      }

      // notify.success("Configuration deleted");
      await queryClient.invalidateQueries({
        queryKey: BILLING_CONFIGURATIONS_QUERY_KEY,
      });
    } catch {
      notify.error("Failed to delete configuration. Please try again.");
    }
  };

  const persistBillingConfiguration = async (options?: {
    mode?: "create" | "update";
  }): Promise<string | null> => {
    const forceCreate = options?.mode === "create";
    const updateId = forceCreate ? null : editingConfigurationId;

    if (!updateId && !projectId) {
      notify.error("Please select a project");
      return null;
    }

    if (invoiceLineItems.length === 0) {
      notify.error("Add at least one item to invoice preview before saving");
      return null;
    }

    if (!clientBillingEmail.trim()) {
      notify.error("Please enter client billing email");
      return null;
    }

    if (!accountantReviewEmail.trim()) {
      notify.error("Please enter accountant review email");
      return null;
    }

    if (!invoiceNumber.trim()) {
      notify.error("Please enter an invoice number");
      return null;
    }

    const clientName =
      selectedProjectClient?.client_name?.trim() || billTo.name?.trim();
    if (!clientName) {
      notify.error("Please select a project with valid client details");
      return null;
    }

    const payload = buildSaveBillingPayload({
      invoiceNumber,
      lineItems: invoiceLineItems,
      billingTypeOptions,
      currencyOptions,
      clientGstType,
      clientBillingEmail,
      ccEmails,
      accountantReviewEmail,
      reminderDays,
      gracePeriodDays,
      nextReminderDate,
      notes,
      projectId: projectId ?? "",
      clientName,
    });

    if (updateId) {
      await PtsBillingService.updateBillingConfiguration(updateId, payload);
      return updateId;
    }

    const response = await PtsBillingService.saveBillingConfiguration(payload);
    const createdId = extractBillingConfigurationId(response);

    if (!createdId) {
      notify.error(
        "Billing configuration saved but no configuration ID was returned"
      );
      return null;
    }

    return createdId;
  };

  const handleSaveConfiguration = async (
    mode: "create" | "update" = editingConfigurationId ? "update" : "create"
  ) => {
    try {
      setIsSavingConfiguration(true);
      const billingConfigurationId = await persistBillingConfiguration({
        mode,
      });

      if (!billingConfigurationId) {
        return;
      }

      notify.success(
        mode === "update"
          ? "Configuration updated successfully"
          : "Configuration added successfully"
      );
      lastLoadedEditKeyRef.current = null;
      resetConfigurationAndInvoicePreview();
      await queryClient.invalidateQueries({
        queryKey: BILLING_CONFIGURATIONS_QUERY_KEY,
      });
      await queryClient.invalidateQueries({
        queryKey: INVOICE_REMINDER_QUERY_KEY,
      });
    } catch (error: unknown) {
      const responseData =
        error &&
        typeof error === "object" &&
        "response" in error &&
        (error as { response?: { data?: unknown } }).response?.data;

      notifyBillingApiErrors(responseData);
    } finally {
      setIsSavingConfiguration(false);
    }
  };

  const handleSaveInvoice = async () => {
    if (!projectId) {
      notify.error("Please select a project");
      return;
    }

    if (!billTo.name?.trim()) {
      notify.error("Please select a project with valid client details");
      return;
    }

    if (!invoiceDate) {
      notify.error("Please enter an invoice date");
      return;
    }

    const clientName =
      selectedProjectClient?.client_name?.trim() || billTo.name?.trim();
    if (!clientName) {
      notify.error("Please select a project with valid client details");
      return;
    }

    try {
      setIsSavingInvoice(true);

      const billingConfigurationId = await persistBillingConfiguration();
      if (!billingConfigurationId) {
        return;
      }

      const payload = buildSaveInvoicePayload({
        invoiceTitle,
        invoiceNumber,
        invoiceDate,
        from: INVOICE_FROM,
        billTo,
        lineItems: invoiceLineItems,
        notes,
        gracePeriodDays,
        projectId,
        clientName,
        clientBillingEmail,
        ccEmails,
        clientGstType,
        billingConfigurationId,
      });

      const response = await PtsBillingService.saveInvoice(payload);

      notify.success(
        extractApiSuccessMessage(response) ?? "Invoice saved successfully"
      );
      await queryClient.invalidateQueries({
        queryKey: BILLING_CONFIGURATIONS_QUERY_KEY,
      });
      await queryClient.invalidateQueries({
        queryKey: INVOICE_PREVIEWS_QUERY_KEY,
      });
    } catch (error: unknown) {
      const responseData =
        error &&
        typeof error === "object" &&
        "response" in error &&
        (error as { response?: { data?: unknown } }).response?.data;

      const validationMessages = extractApiValidationMessages(responseData);
      if (validationMessages.length > 0) {
        notifyBillingApiErrors(responseData);
        return;
      }

      notify.error(
        getApiErrorMessage(error, "Failed to save invoice. Please try again.")
      );
    } finally {
      setIsSavingInvoice(false);
    }
  };

  const buildInvoicePdfPayload = (): InvoicePdfData => ({
    logoSrc: UsLogo,
    invoiceTitle,
    invoiceNumber,
    invoiceDate: formatInvoiceDateForPdf(invoiceDate),
    from: INVOICE_FROM,
    billTo,
    lineItems: invoiceLineItems,
    totals: invoiceTotals,
    dueDays: Number(gracePeriodDays) || DEFAULT_PAYMENT_TERMS.dueDays,
    bankDetails: DEFAULT_PAYMENT_TERMS.bank,
    gstType: clientGstType,
  });

  const handlePrintInvoice = async () => {
    if (invoiceLineItems.length === 0) {
      notify.error("Add at least one line item before printing");
      return;
    }

    try {
      setIsPrinting(true);
      await printInvoicePdf(buildInvoicePdfPayload());
    } catch {
      notify.error("Failed to print invoice. Please try again.");
    } finally {
      setIsPrinting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (invoiceLineItems.length === 0) {
      notify.error("Add at least one line item before downloading PDF");
      return;
    }

    try {
      setIsPdfGenerating(true);
      await downloadInvoicePdf(buildInvoicePdfPayload());
      notify.success("PDF downloaded");
    } catch {
      notify.error("Failed to generate PDF. Please try again.");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-6 xl:grid-cols-[380px_1fr]">
        <Card className="min-w-0 border border-gray-200 bg-white">
          <CardHeader className="flex-row items-center justify-between space-y-0 p-3 md:p-6">
            <div className="flex items-center gap-2">
              <Settings2 className="text-primary h-4 w-4" />
              <CardTitle className="text-base">
                {editingConfigurationId
                  ? "Edit Billing Configuration"
                  : "Billing Configuration"}
              </CardTitle>
            </div>
            {/* <Switch checked={enabled} onCheckedChange={setEnabled} /> */}
          </CardHeader>
          <CardContent className="space-y-4 p-3 md:p-6">
            <div className="space-y-1.5">
              <Label className="text-xs">Project</Label>
              <AntSelect
                showSearch
                allowClear
                placeholder={
                  projectLoading ? "Loading projects..." : "Select project"
                }
                value={projectId}
                onChange={handleProjectChange}
                disabled={projectLoading}
                loading={projectLoading}
                className="billing-project-select w-full [&_.ant-select-selector]:!min-h-9 [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!border-gray-200 [&_.ant-select-selector]:!bg-[#f8f8fe]"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={projectSelectOptions}
                notFoundContent="No projects found"
                listHeight={280}
                popupMatchSelectWidth
                getPopupContainer={trigger =>
                  trigger.parentElement ?? document.body
                }
              />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Billing Type</Label>
                <Select
                  key={`billing-type-${lineItemFormResetKey}`}
                  value={billingType || undefined}
                  onValueChange={setBillingType}
                  disabled={
                    billingTypeLoading || billingTypeOptions.length === 0
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        billingTypeLoading
                          ? "Loading billing types..."
                          : "Select billing type"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {billingTypeOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Frequency</Label>
                <Select
                  key={`frequency-${lineItemFormResetKey}`}
                  value={frequency || undefined}
                  onValueChange={value =>
                    handleFrequencyChange(value as BillingFrequency)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {BILLING_FREQUENCY_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Billing Start Date</Label>
                <Input
                  type="date"
                  value={billingStartDate}
                  onChange={e => {
                    const value = e.target.value;
                    setBillingStartDate(value);
                    // Keep invoice date in sync; user can still change invoice date manually
                    if (value) {
                      setInvoiceDate(value);
                      // Auto-fill next reminder = start date + 15 days (still editable)
                      setNextReminderDate(addDaysToDateInput(value, 15));
                    } else {
                      setNextReminderDate("");
                    }
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Next Reminder Date</Label>
                <Input
                  type="date"
                  value={nextReminderDate}
                  onChange={e => setNextReminderDate(e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Amount</Label>
                <Input
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Currency</Label>
                <Select
                  key={`currency-${lineItemFormResetKey}`}
                  value={currency || undefined}
                  onValueChange={setCurrency}
                  disabled={currencyLoading || currencyOptions.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        currencyLoading
                          ? "Loading currencies..."
                          : "Select currency"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {currencyOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">HSN/SAC</Label>
                <Input
                  value={hsnSac}
                  onChange={e => setHsnSac(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Qty</Label>
                <Input
                  value={qty}
                  onChange={e => setQty(e.target.value)}
                  type="number"
                  min="1"
                />
              </div>
            </div>

            <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
              <div className="flex-1">
                <p className="text-sm font-medium">GST Applicable</p>
                <p className="text-muted-foreground text-[11px]">
                  {!projectId
                    ? "Select a project to load GST settings"
                    : projectClientLoading
                      ? "Loading GST settings from project..."
                      : isInstateGst
                        ? "CGST + SGST for in-state clients"
                        : "IGST for out-of-state clients"}
                </p>
              </div>
              <Input
                className="w-14 text-center"
                value={gstRate}
                onChange={e => setGstRate(e.target.value)}
                disabled={!projectId || projectClientLoading}
              />
              <Switch
                checked={gstApplicable}
                onCheckedChange={setGstApplicable}
                disabled={!projectId || projectClientLoading}
              />
            </div>

            <Button
              type="button"
              variant="outline"
              className="flex w-full items-center gap-2"
              onClick={handleAddToPreview}
            >
              <Plus className="h-4 w-4" />
              {editingLineItemId
                ? "Update Invoice Item"
                : "Add to Invoice Preview"}
            </Button>

            {editingLineItemId && (
              <Button
                type="button"
                variant="ghost"
                className="w-full text-xs underline"
                onClick={handleCancelLineItemEdit}
              >
                Cancel edit
              </Button>
            )}

            {invoiceLineItems.length > 0 && (
              <div className="overflow-hidden rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead className="text-primary h-9 text-xs">
                        Name
                      </TableHead>
                      <TableHead className="text-primary h-9 text-right text-xs">
                        Amount
                      </TableHead>
                      <TableHead className="text-primary h-9 w-20 text-center text-xs">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoiceLineItems.map(item => (
                      <TableRow
                        key={item.id}
                        className={
                          editingLineItemId === item.id
                            ? "bg-primary/5"
                            : undefined
                        }
                      >
                        <TableCell className="max-w-[140px] py-2">
                          <p className="truncate text-sm font-medium">
                            {item.title}
                          </p>
                        </TableCell>
                        <TableCell className="py-2 text-right text-sm font-medium">
                          {inr(item.amount)}
                        </TableCell>
                        <TableCell className="py-2">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-blue-600 hover:bg-blue-50 hover:text-blue-700 [&_svg]:text-blue-600"
                              onClick={() => handleEditLineItem(item)}
                              title="Edit invoice item"
                            >
                              <Edit2 className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700 [&_svg]:text-red-600"
                              onClick={() => handleDeleteLineItem(item.id)}
                              title="Delete invoice item"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Client Billing Email</Label>
              <Input
                value={clientBillingEmail}
                onChange={e => setClientBillingEmail(e.target.value)}
                placeholder="client@example.com"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">CC Emails</Label>
              <Input
                value={ccEmails}
                onChange={e => setCcEmails(e.target.value)}
                placeholder="email1@example.com, email2@example.com"
              />
              <p className="text-muted-foreground text-[11px]">
                Comma-separated
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Accountant Review Email</Label>
              <Input
                value={accountantReviewEmail}
                onChange={e => setAccountantReviewEmail(e.target.value)}
                placeholder="accountant@example.com"
              />
            </div>

            <div className="grid gap-3 md:grid-cols-1">
              {/* <div className="space-y-1.5">
                <Label className="text-xs">Reminder (days before)</Label>
                <Input
                  value={reminderDays}
                  onChange={e => setReminderDays(e.target.value)}
                  type="number"
                  min="0"
                />
              </div> */}
              <div className="space-y-1.5">
                <Label className="text-xs">Grace Period (days)</Label>
                <Input
                  value={gracePeriodDays}
                  onChange={e => setGracePeriodDays(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
              />
            </div>

            {editingConfigurationId ? (
              <div className="space-y-2">
                <Button
                  className="bg-primary hover:bg-primary/90 w-full text-white"
                  disabled={isSavingConfiguration}
                  onClick={() => void handleSaveConfiguration("update")}
                >
                  {isSavingConfiguration ? "Saving..." : "Update Configuration"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={isSavingConfiguration}
                  onClick={() => void handleSaveConfiguration("create")}
                >
                  {isSavingConfiguration ? "Saving..." : "Add Configuration"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-xs underline"
                  onClick={handleCancelConfigurationEdit}
                >
                  Cancel configuration edit
                </Button>
              </div>
            ) : (
              <Button
                className="bg-primary hover:bg-primary/90 w-full text-white"
                disabled={isSavingConfiguration}
                onClick={() => void handleSaveConfiguration("create")}
              >
                {isSavingConfiguration ? "Saving..." : "Save Configuration"}
              </Button>
            )}
          </CardContent>
        </Card>

        <Card className="min-w-0 border border-gray-200 bg-white">
          <CardHeader className="flex flex-col gap-3 space-y-0 p-3 sm:flex-row sm:items-center sm:justify-between md:p-6">
            <CardTitle className="text-base">Invoice Preview</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={isSavingInvoice}
                onClick={handleSaveInvoice}
              >
                <Save className="h-3.5 w-3.5" />
                {isSavingInvoice ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={isPrinting}
                onClick={handlePrintInvoice}
              >
                <Printer className="h-3.5 w-3.5" />
                {isPrinting ? "Preparing..." : "Print"}
              </Button>
              <Button
                size="sm"
                className="bg-primary hover:bg-primary/90 gap-2 text-white"
                disabled={isPdfGenerating}
                onClick={handleDownloadPdf}
              >
                <Download className="h-3.5 w-3.5" />
                {isPdfGenerating ? "Generating..." : "PDF"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-hidden p-3 md:p-6">
            <div className="bg-card flex min-h-[1050px] flex-col space-y-6 rounded-lg border p-4 sm:p-8">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <img
                  src={UsLogo}
                  alt="Universal Software"
                  className="h-16 w-auto max-w-[260px] object-contain object-left"
                />
                <div className="text-right">
                  <Input
                    value={invoiceTitle}
                    onChange={e => setInvoiceTitle(e.target.value)}
                    className="text-primary h-8 px-2 text-right text-sm font-bold uppercase"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 pt-4 text-xs sm:grid-cols-2">
                <div>
                  <p className="text-muted-foreground">INVOICE #</p>
                  <Input
                    value={invoiceNumber}
                    onChange={e => setInvoiceNumber(e.target.value)}
                    className="text-primary mt-1 h-7 px-2 font-semibold"
                  />
                </div>
                <div>
                  <p className="text-muted-foreground">INVOICE DATE</p>
                  <Input
                    type="date"
                    value={invoiceDate}
                    onChange={e => setInvoiceDate(e.target.value)}
                    className="mt-1 h-7 px-2 font-semibold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-0.5 break-words rounded-md border border-gray-300 p-3 text-xs">
                  <p className="text-muted-foreground font-semibold">FROM</p>
                  <p className="text-foreground font-semibold">
                    {INVOICE_FROM.name}
                  </p>
                  {INVOICE_FROM.lines.map(line => (
                    <p key={line}>{line}</p>
                  ))}
                  {INVOICE_FROM.gstin ? (
                    <p className="mt-1">GST NO: {INVOICE_FROM.gstin}</p>
                  ) : null}
                </div>
                <div className="space-y-0.5 break-words rounded-md border border-gray-300 p-3 text-xs">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <p className="text-muted-foreground font-semibold">
                      BILL TO
                    </p>
                    {projectClients.length > 1 && (
                      <AntSelect
                        size="small"
                        showSearch
                        value={selectedClientId}
                        onChange={value => setSelectedClientId(value)}
                        options={clientSelectOptions}
                        optionFilterProp="label"
                        className="min-w-[140px] [&_.ant-select-selector]:!rounded-md"
                        popupMatchSelectWidth={false}
                        getPopupContainer={trigger =>
                          trigger.parentElement ?? document.body
                        }
                      />
                    )}
                  </div>
                  {billTo.name ? (
                    <p className="text-foreground font-semibold">
                      {billTo.name}
                    </p>
                  ) : null}
                  {billTo.lines.map(line => (
                    <p key={line}>{line}</p>
                  ))}
                  {billTo.gstin ? (
                    <p className="mt-1">GST NO: {billTo.gstin}</p>
                  ) : null}
                </div>
              </div>

              <div className="overflow-x-auto rounded-md">
                <Table className="[&_tbody_tr]:border-0">
                  <TableHeader>
                    <TableRow className="bg-muted/60 hover:bg-muted/60 border-b border-gray-300">
                      <TableHead className="text-primary">#</TableHead>
                      <TableHead className="text-primary">
                        Description
                      </TableHead>
                      <TableHead className="text-primary">HSN/SAC</TableHead>
                      <TableHead className="text-primary text-right">
                        Qty
                      </TableHead>
                      <TableHead className="text-primary text-right">
                        Rate
                      </TableHead>
                      <TableHead className="text-primary text-right">
                        Amount
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoiceLineItems.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-muted-foreground py-8 text-center text-sm"
                        >
                          Enter billing details and click &quot;Add to Invoice
                          Preview&quot; to add line items
                        </TableCell>
                      </TableRow>
                    ) : (
                      invoiceLineItems.map((item, index) => {
                        const tax = calculateLineItemTax(
                          item.amount,
                          item.gstApplicable,
                          item.gstRate,
                          clientGstType
                        );
                        const taxRowCount = getGstTaxRowCount(
                          item.gstApplicable,
                          clientGstType
                        );
                        const itemRowSpan = item.gstApplicable
                          ? taxRowCount + 1
                          : 1;
                        const formatRateLabel = (rate: number) =>
                          Number.isInteger(rate)
                            ? String(rate)
                            : rate.toFixed(1);
                        const taxRowClass = "border-0 hover:bg-transparent";
                        const taxCellClass = "border-0 py-1";
                        const taxLastRowCellClass = "border-0 py-1";

                        return (
                          <Fragment key={item.id}>
                            <TableRow className="border-0 hover:bg-transparent">
                              <TableCell rowSpan={itemRowSpan}>
                                {index + 1}
                              </TableCell>
                              <TableCell>
                                <p className="font-medium">{item.title}</p>
                                {(item.billingPeriod || item.subtitle) && (
                                  <p className="mt-1 text-xs">
                                    {item.billingPeriod ? (
                                      <span className="text-foreground font-medium">
                                        ({item.billingPeriod})
                                      </span>
                                    ) : null}
                                    {item.subtitle ? (
                                      <span className="text-muted-foreground">
                                        {item.billingPeriod ? " · " : ""}
                                        {item.subtitle}
                                      </span>
                                    ) : null}
                                  </p>
                                )}
                              </TableCell>
                              <TableCell
                                rowSpan={itemRowSpan}
                                className="text-blue-600"
                              >
                                {item.hsnSac}
                              </TableCell>
                              <TableCell
                                rowSpan={itemRowSpan}
                                className="text-right"
                              >
                                {item.qty}
                              </TableCell>
                              <TableCell
                                rowSpan={itemRowSpan}
                                className="text-right"
                              >
                                {formatInvoiceAmount(item.rate)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatInvoiceAmount(item.amount)}
                              </TableCell>
                            </TableRow>
                            {item.gstApplicable && tax.isInstate && (
                              <>
                                <TableRow className={taxRowClass}>
                                  <TableCell
                                    colSpan={4}
                                    className={`text-muted-foreground text-left text-xs ${taxCellClass}`}
                                  >
                                    CGST {formatRateLabel(tax.halfGstRate)}%
                                  </TableCell>
                                  <TableCell
                                    className={`text-right text-xs text-blue-600 ${taxCellClass}`}
                                  >
                                    {formatInvoiceAmount(tax.cgst)}
                                  </TableCell>
                                </TableRow>
                                <TableRow className={taxRowClass}>
                                  <TableCell
                                    colSpan={4}
                                    className={`text-muted-foreground text-left text-xs ${taxLastRowCellClass}`}
                                  >
                                    SGST {formatRateLabel(tax.halfGstRate)}%
                                  </TableCell>
                                  <TableCell
                                    className={`text-right text-xs text-blue-600 ${taxLastRowCellClass}`}
                                  >
                                    {formatInvoiceAmount(tax.sgst)}
                                  </TableCell>
                                </TableRow>
                              </>
                            )}
                            {item.gstApplicable && !tax.isInstate && (
                              <TableRow className={taxRowClass}>
                                <TableCell
                                  colSpan={4}
                                  className={`text-muted-foreground text-left text-xs ${taxLastRowCellClass}`}
                                >
                                  IGST {formatRateLabel(tax.fullGstRate)}%
                                </TableCell>
                                <TableCell
                                  className={`text-right text-xs text-blue-600 ${taxLastRowCellClass}`}
                                >
                                  {formatInvoiceAmount(tax.igst)}
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end">
                <div className="w-72 space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sub Total</span>
                    <span>{inr(invoiceTotals.subtotal)}</span>
                  </div>
                  {invoiceTotals.taxTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tax Total</span>
                      <span>{inr(invoiceTotals.taxTotal)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-2 font-bold">
                    <span>Total</span>
                    <span>{inr(invoiceTotals.total)}</span>
                  </div>
                </div>
              </div>

              <InvoicePreviewPaymentSection
                dueDays={
                  Number(gracePeriodDays) || DEFAULT_PAYMENT_TERMS.dueDays
                }
                bankDetails={DEFAULT_PAYMENT_TERMS.bank}
              />

              <div className="mt-auto pt-10 text-center">
                <p className="text-muted-foreground text-[11px]">
                  This is a computer-generated invoice and does not require a
                  physical signature
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="config-listing" className="space-y-4">
        <div className="flex justify-center overflow-x-auto px-2 md:px-0">
          <PtsPillTabsList className="w-max md:w-1/2">
            <PtsPillTabsTrigger
              value="config-listing"
              className="whitespace-nowrap"
            >
              <List className="h-4 w-4 shrink-0" />
              Billing Configuration Listing
            </PtsPillTabsTrigger>
            <PtsPillTabsTrigger
              value="preview-listing"
              className="whitespace-nowrap"
            >
              <FileText className="h-4 w-4 shrink-0" />
              Invoice Preview Listing
            </PtsPillTabsTrigger>
          </PtsPillTabsList>
        </div>
        <TabsContent value="config-listing">
          <BillingConfigListingSection
            onEdit={handleEditBillingConfiguration}
            onDelete={handleDeleteBillingConfiguration}
            editingConfigurationId={editingConfigurationId}
          />
        </TabsContent>
        <TabsContent value="preview-listing">
          <InvoicePreviewListingSection />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BillingConfigTab;
